import { getSDK } from '../functions/sdk.js'
import { AI_MODEL_ROWS, aiModelCatalogRows } from '../functions/aiModelCatalog.js'
// THE house keyboard model for menus — functions/menuKeyboard.js. Held as
// scope VALUES (never bare identifiers inside a factory — FA204).
import {
  menuArrowNav,
  menuFocusOnOpen,
  menuNodeVisible,
  restoreMenuFocus
} from '../functions/menuKeyboard.js'

// AiSettingsPanel — the SINGLE AI-settings body shared by BOTH assistant
// surfaces so they can never drift:
//   • the canvas prompt bar's ParamsBtn dropdown (workspace/app.js →
//     openDropdown({ content: { extends: 'AiSettingsPanel' } }))
//   • the sidebar assistant's SettingsDrawer sheet (_simone/SettingsDrawer.js)
//
// The panel does NOT choose the MODEL. The model DROPDOWN (AiModelSwitcher —
// the assistant header pill and the canvas composer's ModelBtn) is the single
// model chooser (2026-08-31, Nika: "remove models list in sidebar ai settings,
// we already have dropdown for it"). The provider list that used to live here
// rendered the SAME eight rows as that dropdown and wrote the SAME
// `root.modelMode` + `symbols_ai_model_mode` key — one chooser duplicated on
// one surface. It is deleted; what stays is every control the dropdown does
// NOT carry, each wired to the state that actually routes a turn:
//   • local agent  → targetMode 'local' (the simone-bridge WebSocket round-trip
//                    in simoneAiTransport); toggled via the onTargetModeChange
//                    host hook so the bridge socket opens/closes. The row is a
//                    TOGGLE because the model dropdown HIDES itself in local
//                    mode (Chat.js Actions, app.js ModelBtn) — this row is the
//                    only way back to a cloud provider, a job the deleted
//                    provider list used to do.
//   • authorization→ sdk.ai.getAuthMode/setAuthMode.
//   • SCOPE        → 'me' (per-user localStorage, the above) vs 'workspace'
//                    (an admin pins settings for every member via
//                    settings.aiProvider).
//   • workspace pin→ PinSection (workspace scope only): ONE row + an
//                    on-demand picker writing aiProvider.mode 'provider' +
//                    modelMode — the only shape server
//                    resolveWorkspaceModelMode honours for a model
//                    (AI-SETTINGS-WORKSPACE-PROVIDER-PIN-NO-UI-WRITER-1: the
//                    deleted list was the pin's only writer; this row
//                    restores it WITHOUT a standing list — the catalog
//                    mounts only while the picker is open, and it is the
//                    dropdown's own shared catalog, never a second copy).
//
// State keys written here (declared with safe defaults in workspace/state-app.js):
//   _aiScope ('me'|'workspace'), targetMode, _aiBridgeSetupOpen, _aiPanelTick
//   (nudge signal for reactive re-eval), _aiPinMenuOpen (the pin picker's
//   gate). `modelMode` is READ nowhere in this file any more and written
//   only by the model dropdown. One key lives on the panel's OWN store, never
//   the root: `pinError` (a refused workspace write — see the body's `state`).

// Section header — THE shared menu caption (AiMenu.js AiMenuHeading — the
// pattern this file originated), so AI-settings sections, the model menu and
// the assistant Sessions menu all read as one treatment. Called immediately
// (not read from inside a serialized factory closure) so it's not a frank
// trap — the returned plain object is what lands in the tree, not this
// function reference.
const sectionHeader = (key) => ({
  extends: 'AiMenuHeading',
  // The caption arrives as a polyglot KEY. `translate` returns its input
  // unchanged when a key does not resolve, so a plain caption still renders.
  text: (el) => el.call('polyglot', key)
})

// One option row — THE shared AiOptionRow (AiMenu.js): MenuPanelItem metrics
// (Y/Z1 padding, radiusControl), the house active pattern (`selected` wash +
// `title` ink), disabled rows that stay real click targets with a fixed
// one-line Sub slot (a demoted row never grows and shifts its neighbours —
// Nika: status must never shift layout). `childrenAs: 'state'` feeds each row
// { key, label, sub, active, disabled }; onClick is provided per section.
// Called immediately (same reasoning as sectionHeader above) — not a trap.
const optionRow = (onClick) => ({
  extends: 'AiOptionRow',
  onClick
})

// ── Answers: Page · Sidebar · Minimized (ASSISTANT_BOARD_MODE_SPEC §1) ────
// WHERE the assistant's answers go — THE one segmented choice, shown on both
// hosts of this panel (the composer's settings menu and the sidebar's
// settings sheet) because both render this body. The kit segmented control
// at VALUE strength (the ink thumb, compact). The value is the user's own
// (never workspace-scoped), so it sits above "Applies to". The writer is the
// shell's `setAssistantAnswersMode` (workspace functions/assistantDock.js):
// persisted per user; Sidebar returns to the last side.
//
// Hidden only where the shell's writer is absent (another app mounting this
// panel). Page is a PLACEMENT (the main thread, the default — Nika's pick
// 6 A): it needs no board service, so the choice shows whether or not the
// generated-board service answers (only the Dashboard MODE needs it).
const AnswersSection = {
  hide: (el) =>
    typeof (el.context && el.context.functions && el.context.functions.setAssistantAnswersMode) !==
    'function',
  subscribeTo: ['assistantDock'],
  flow: 'y',
  gap: 'Y',
  SectionHeader: sectionHeader('ai.answers.label'),
  AppSegmented: {
    variant: 'choice',
    compact: true,
    ariaLabel: (el) => el.call('polyglot', 'ai.answers.label'),
    current: (el, s) => {
      const dock = s.root && s.root.assistantDock
      return dock === 'main' ? 'page' : dock === 'minimized' ? 'minimized' : 'sidebar'
    },
    select: 'setAssistantAnswersMode',
    childrenAs: 'state',
    children: () => [
      { key: 'page', text: 'ai.answers.page', value: 'page' },
      { key: 'sidebar', text: 'ai.answers.sidebar', value: 'sidebar' },
      { key: 'minimized', text: 'ai.answers.minimized', value: 'minimized' }
    ]
  },
  Note: {
    tag: 'span',
    fontSize: 'Y',
    color: 'caption',
    lineHeight: '1.4',
    text: (el) => el.call('polyglot', 'ai.answers.note')
  }
}

// ── Scope segmented control (Just me / Whole workspace) ──────────────────
const ScopeSection = {
  hide: (el, s) => !(s.root.workspace || s.root.workspaceEmbed),
  flow: 'y',
  gap: 'Y',
  SectionHeader: sectionHeader('ai.settings.appliesTo'),
  Seg: {
    flow: 'x',
    gap: 'W',
    padding: 'W',
    round: 'radiusPill',
    background: 'overlay',
    // A segmented control is a radio group, so the keys are LEFT/RIGHT, not
    // up/down (`orientation: 'x'`), and the walk is over the segment buttons
    // themselves rather than the `[role="menuitem"]` default — these are
    // radios, not menu items. Home/End jump to the ends.
    role: 'radiogroup',
    ariaLabel: (el) => el.call('polyglot', 'ai.settings.appliesTo'),
    onKeydown: (e, el) => el.scope.navRows(e, el.node, { orientation: 'x', selector: 'button' }),
    children: (el, s) => {
      void s.root._aiPanelTick
      const cur = s.root._aiScope || 'me'
      const admin = el.scope.isWorkspaceAdmin(s)
      return [
        { key: 'me', label: 'ai.settings.scope.me', enabled: true },
        { key: 'workspace', label: 'ai.settings.scope.workspace', enabled: admin }
      ].map((o) => ({ ...o, active: o.key === cur }))
    },
    childrenAs: 'state',
    childExtends: {
      tag: 'button',
      type: 'button',
      role: 'radio',
      ariaChecked: (el, s) => (s.active ? 'true' : 'false'),
      // Not an admin → not selectable. `aria-disabled` (never the `disabled`
      // attribute) keeps the row a real target whose `title` reason stays
      // readable, and `menuRowNodes` skips it, so the arrow cycle never
      // stops on a segment the click path would refuse anyway.
      ariaDisabled: (el, s) => (s.enabled ? null : 'true'),
      flex: '1',
      minHeight: 'controlHCompact',
      padding: '0 Z',
      round: 'radiusPill',
      border: 'none',
      textAlign: 'center',
      whiteSpace: 'nowrap',
      fontWeight: '700',
      fontSize: 'Z',
      cursor: (el, s) => (s.enabled ? 'pointer' : 'not-allowed'),
      opacity: (el, s) => (s.enabled ? '1' : '.4'),
      color: (el, s) => (s.active ? 'title' : 'caption'),
      // House active wash; no transition on the reactive background (it
      // re-applies per tick and would restart the animation forever).
      background: (el, s) => (s.active ? 'selected' : 'transparent'),
      text: (el, s) => el.call('polyglot', s.label || ''),
      title: (el, s) => (s.enabled ? '' : el.call('polyglot', 'ai.settings.scopeAdminOnly')),
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        if (!s.enabled) return
        const root = el.state && el.state.root
        if (root && root.update) root.update({ _aiScope: s.key })
        el.scope.bumpTick(root)
      }
    }
  },
  Note: {
    tag: 'span',
    fontSize: 'Y',
    color: 'caption',
    lineHeight: '1.4',
    margin: 'X2 0 0 0',
    text: (el, s) =>
      el.call(
        'polyglot',
        el.scope.scopeIsWorkspace(s)
          ? 'ai.settings.scopeNote.workspace'
          : 'ai.settings.scopeNote.me'
      )
  }
}

// ── Workspace model pin (workspace scope only) ───────────────────────────
// The admin control the deleted provider list used to carry: settings
// .aiProvider mode 'provider' + modelMode — the ONE shape server
// resolveWorkspaceModelMode honours ahead of every member's own dropdown
// choice (AI-SETTINGS-WORKSPACE-PROVIDER-PIN-NO-UI-WRITER-1). ONE row; the
// catalog appears only in an on-demand picker (never a standing list —
// Nika's removal), and it is the model dropdown's own shared catalog
// (functions/aiModelCatalog.js), demoted by the same live diagnostics.
const PinSection = {
  hide: (el, s) => !el.scope.scopeIsWorkspace(s),
  flow: 'y',
  gap: 'Y',
  position: 'relative',
  SectionHeader: sectionHeader('ai.settings.workspaceModel'),
  PinRow: {
    extends: 'AiOptionRow',
    cursor: 'pointer',
    opacity: '1',
    title: '',
    background: (el, s) => (s.root._aiPinMenuOpen ? 'selected' : 'transparent'),
    ':hover': { background: 'hover' },
    // Menu-button contract: this row OPENS a picker, so it announces the
    // popup and its state, and ArrowDown/ArrowUp open it (Enter/Space
    // already do — AiOptionRow is a native <button>).
    ariaHaspopup: 'menu',
    ariaExpanded: (el, s) => (s.root._aiPinMenuOpen ? 'true' : 'false'),
    onKeydown: (e, el, s) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
      e.preventDefault()
      e.stopPropagation()
      if (!s.root._aiPinMenuOpen) el.scope.togglePinMenu(el, s)
    },
    onClick: (e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      el.scope.togglePinMenu(el, s)
    },
    // Same named-child anatomy pins as LocalRow (the row base reads per-row
    // state; this named child's `s` is the whole panel state).
    Icon: { if: () => false },
    Meta: { if: () => false },
    Body: {
      Label: { text: '{{ ai.settings.pinnedModel | polyglot }}' },
      // Sub swaps its one line (pinned label vs. Off vs. a refused save) —
      // the row never grows and shifts its neighbours, the house rule every
      // AiOptionRow follows. A refused save reads in the AA-safe danger ink
      // and is announced: a polite live region, because the focus is back on
      // this row when the refusal lands.
      Sub: {
        tag: 'div',
        ariaLive: 'polite',
        color: (el, s) => (el.scope.pinFailed(el, s) ? 'dangerInk' : 'caption'),
        text: (el, s) => el.scope.pinSubCopy(el, s)
      }
    },
    Icon_1: {
      hide: (el, s) => !el.scope.pinnedModelMode(el, s)
    }
  },
  // The picker — MOUNT-gated (`if:`), so the catalog exists only while open
  // and the panel never carries a standing model list (the intent
  // aiSettingsPanelNoModelsList.test.js locks; this gate is what keeps the
  // pin surface inside it). Anchored panel = the AiModelSwitcher composite
  // recipe (AiMenuPanel base + a document-level outside-click guard).
  Picker: {
    extends: 'AiMenuPanel',
    if: (el, s) => !!s.root._aiPinMenuOpen,
    isOpen: (el, s) => !!s.root._aiPinMenuOpen,
    tag: 'div',
    position: 'absolute',
    top: '100%',
    left: '0',
    right: '0',
    zIndex: '200',
    // A real menu (2026-09-02): focus enters on open, arrows cycle the model
    // rows, Escape closes and hands focus back to PinRow. `if:`-gated, so the
    // node exists only while open — the open edge is the mount itself, and
    // the close edge is the unmount, which is why the focus return rides
    // onRemove rather than a true→false state edge.
    role: 'menu',
    tabindex: '-1',
    ariaLabel: (el) => el.call('polyglot', 'ai.settings.pinModel'),
    onRender: (el, s) =>
      el.scope.focusOnOpen(el, !!s.root._aiPinMenuOpen, {
        when: (node) => el.scope.nodeVisible(node)
      }),
    onKeydown: (e, el, s) => {
      if (el.scope.navRows(e, el.node)) return
      if (e.key !== 'Escape') return
      // Stop here so the PANEL's Escape (which closes the whole settings
      // body) never fires in the same keystroke: one Escape closes one level.
      e.preventDefault()
      e.stopPropagation()
      if (s.root && s.root._aiPinMenuOpen) s.root.update({ _aiPinMenuOpen: false })
      el.scope.focusBack(el)
    },
    onClick: (e) => e.stopPropagation(),
    // Outside-press closes. FA503: declared, so DOMQL registers it once and
    // removes it in dispose() — no `el._docClickGuard` mirror field.
    onDocumentMousedown: {
      capture: true,
      handler: (ev, el) => {
        // Guard against the SECTION (row + picker), not the picker node
        // alone: a mousedown on the PinRow would otherwise close the menu
        // here and the row's click would immediately reopen it — the toggle
        // could then never close (the AiModelSwitcher trigger has that
        // artifact; this section does not inherit it).
        const node = el && el.node
        const host = (node && node.parentNode) || node
        if (!host || host.contains(ev.target)) return
        const root = el.state && el.state.root
        if (root && root._aiPinMenuOpen) root.update({ _aiPinMenuOpen: false })
      }
    },
    onRemove: (el) => {
      // The picker unmounts on close (`if:` gate) — that unmount IS the close
      // edge, so the focus return to PinRow rides it.
      el.scope.focusBack(el)
    },
    PvHeading: {
      extends: 'AiMenuHeading',
      text: '{{ ai.settings.pinModel | polyglot }}'
    },
    Options: {
      flow: 'y',
      gap: 'V',
      children: (el, s) => el.scope.pinRows(el, s),
      childrenAs: 'state',
      childExtends: optionRow((e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        // A demoted row stays a real click target (its reason is readable),
        // just not a selectable one — the model dropdown's own contract.
        if (s.disabled) return
        el.scope.choosePin(el, s, s.key)
      })
    }
  }
}

// ── Local agent — the one target the model dropdown cannot reach ─────────
// The MODEL LIST that used to open this section is gone (see the file header):
// the model dropdown owns model choice. What remains is the target the
// dropdown has no concept of — the machine-local simone-bridge agent.
//
// The whole SECTION hides at workspace scope (a shared workspace can't run on
// one member's laptop bridge). The gate sits on the section, not on the row:
// with the list removed the row is the section's only content, so a row-level
// gate would leave a heading standing over nothing.
// EXPORTED (2026-09-02): unmounted from this panel — the Local agent row
// moved into AiModelSwitcherBody (both AI surfaces render that body). The
// export keeps the section registered for any host that still mounts it.
export const ProviderSection = {
  hide: (el, s) => el.scope.scopeIsWorkspace(s),
  flow: 'y',
  gap: 'Y',
  SectionHeader: sectionHeader('ai.settings.provider'),
  // THE shared AiOptionRow — a named child (not a childrenAs:'state' row), so
  // the state-reading factories are overridden with the local predicates and
  // copy.
  LocalRow: {
    extends: 'AiOptionRow',
    cursor: 'pointer',
    opacity: '1',
    title: '',
    background: (el, s) => (el.scope.isLocal(s) ? 'selected' : 'transparent'),
    ':hover': { background: 'hover' },
    onClick: (e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      el.scope.chooseLocal(el, s)
    },
    // The row base reads per-row state for these slots; this named child's
    // `s` is the whole panel state, so pin them off deterministically.
    // (Icon = leading glyph slot, Icon_1 = trailing check — AiOptionRow.)
    Icon: { if: () => false },
    Meta: { if: () => false },
    Body: {
      Label: { text: '{{ ai.settings.localAgent | polyglot }}' },
      // Sub SWAPS its text while local instead of gaining a second line, so
      // the row never grows and shifts its neighbours (the house rule every
      // AiOptionRow follows). The swapped copy is what makes the toggle
      // discoverable: the header model dropdown is hidden in local mode, so
      // the user must know this row is also the way out.
      Sub: {
        tag: 'div',
        text: (el, s) =>
          el.call(
            'polyglot',
            el.scope.isLocal(s) ? 'ai.settings.localAgentSubActive' : 'ai.settings.localAgentSub'
          )
      }
    },
    Icon_1: {
      hide: (el, s) => !el.scope.isLocal(s)
    }
  }
}

// The local-bridge SETUP lives in AiBridgeModal (a shell-root overlay opened by
// chooseLocal via root._aiBridgeSetupOpen) — NOT inline here, so selecting the
// local agent never grows/shifts the dropdown.

// ── Authorization (real ask/auto) ────────────────────────────────────────
const AuthSection = {
  flow: 'y',
  gap: 'Y',
  SectionHeader: sectionHeader('ai.settings.authorization'),
  Options: {
    flow: 'y',
    gap: 'V',
    // Arrows cycle within THIS section only (the walk is scoped to this
    // node), so Authorization and Voice never fold into one another's cycle.
    // The rows are AiOptionRow <button>s carrying role="menuitem", so
    // Enter/Space already activate them.
    role: 'group',
    ariaLabel: (el) => el.call('polyglot', 'ai.settings.authorization'),
    onKeydown: (e, el) => el.scope.navRows(e, el.node),
    children: (el, s) => {
      void s.root._aiPanelTick
      const ai = el.call('getAiService')
      const pin = el.scope.scopeIsWorkspace(s) ? el.scope.workspacePin(el, s) : null
      const cur =
        pin && pin.authMode ? pin.authMode : (ai && ai.getAuthMode && ai.getAuthMode()) || 'ask'
      return el.scope.AUTH_ROWS.map((r) => ({ ...r, active: r.key === cur }))
    },
    childrenAs: 'state',
    childExtends: optionRow((e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      el.scope.chooseAuth(el, s, s.key)
    })
  }
}

// Voice — whether Simone SPEAKS its replies (ElevenLabs TTS through the
// existing /core/ai/voice/tts chain; speakReply.js owns playback). Same row
// mechanics as AuthSection; current value lives in localStorage via
// speakRepliesEnabled/setSpeakReplies (default OFF — S3, tickets/sonnet.md,
// 2026-08-16: a fresh profile must not speak until the user opts in).
const VoiceSection = {
  flow: 'y',
  gap: 'Y',
  SectionHeader: sectionHeader('ai.settings.voiceGroup'),
  Options: {
    flow: 'y',
    gap: 'V',
    role: 'group',
    ariaLabel: (el) => el.call('polyglot', 'ai.settings.voiceGroup'),
    onKeydown: (e, el) => el.scope.navRows(e, el.node),
    children: (el, s) => {
      void s.root._aiPanelTick
      let on = false
      try {
        on = !!el.call('speakRepliesEnabled')
      } catch (_) {}
      const cur = on ? 'on' : 'off'
      return el.scope.VOICE_ROWS.map((r) => ({ ...r, active: r.key === cur }))
    },
    childrenAs: 'state',
    childExtends: optionRow((e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      el.scope.chooseVoice(el, s, s.key)
    })
  }
}

// ── AI reliability (AI-210 "give the turn telemetry a reader") ───────────
// Quiet, READ-ONLY 14-day tool-turn success readout — the settings-panel
// half of AI-210 (the admin/usage dashboard half already shipped: see
// workspace/packages/workspace/pages/admin/usage.js Section_AiTurnStats).
// A ONE-LINE stat (title ink) + a caption-ink detail line, matching
// AiOptionRow's Label/Sub body shape — a settings panel, not a dashboard,
// so no per-day breakdown here.
//
// Genuinely LOCAL to this panel instance: unlike every section above (whose
// state keys are cross-cutting, app-root-owned settings — see the file
// header), this is ephemeral per-mount telemetry that must never land on
// the shared root store both panel hosts read/write through `s.root.X`.
// The explicit `state: {}` below (vs. every section above, which declares
// no `state:` and so inherits the parent's store BY REFERENCE — see
// @symbo.ls/state src/create.js `applyInitialState`) makes a genuinely
// separate store branch: `el.update` in onInit writes ONLY here. `s.root`
// reads from inside this branch still resolve to the real app root (the
// store's `root` getter climbs past local branches to the topmost store —
// @symbo.ls/state src/methods.js), so nothing above is affected.
const ReliabilitySection = {
  flow: 'y',
  gap: 'Y',
  state: {},
  SectionHeader: sectionHeader('ai.settings.reliabilityGroup'),
  // Best-effort, fire-and-forget — it never blocks panel open. A missing SDK
  // method or a failed call both just leave `aiReliability` unset; Row's `if:`
  // then never activates — the same optional-call contract every other row in
  // this panel follows.
  onInit: (el) => {
    el.scope.loadAiReliability(el)
  },
  Row: {
    // Lazy activation: absent at first render, created the moment
    // loadAiReliability's `.then()` populates `aiReliability` — v3.14's
    // `if:` effect re-runs the full creation path on a false→true flip
    // (@symbo.ls/element src/create.js registerIfEffect "First-time
    // activation"), so this resurrects correctly once the async call
    // resolves; it does not need `hide:` + `subscribeTo`. Never reads a
    // MISSING key as "empty" — the empty-state copy is a POPULATED value
    // (formatAiReliability returns it for a genuine `rows: []`), so this
    // stays un-activated only pre-load or when the SDK lacks the method.
    if: (el, s) => !!s.aiReliability,
    tag: 'div',
    flow: 'y',
    gap: 'V',
    padding: 'Y Z1',
    Label: {
      tag: 'span',
      fontWeight: '700',
      color: 'title',
      fontSize: 'Z1',
      lineHeight: '1.3',
      text: (el, s) => s.aiReliability.label
    },
    Sub: {
      tag: 'span',
      fontSize: 'Y',
      color: 'caption',
      lineHeight: '1.4',
      text: (el, s) => s.aiReliability.sub
    }
  }
}

// The composed body. No positioning / no outside-click guard — the host (the
// dropdown plugin panel OR the SettingsDrawer sheet) owns those. Both hosts
// mount this exact object, so the two surfaces are identical by construction.
export const AiSettingsPanel = {
  // FA204 / frank trap — every helper below used to be a bare module-scope
  // const/function read from inside nested factories (`children`, `onClick`,
  // `background`, …). Those read as undefined once this component was
  // frank.toJSON'd for platform delivery (workspace d6ea1142,
  // project_frank_drops_module_local_helpers). Moved into `scope`, called via
  // `el.scope.<name>`; helpers that call SIBLING scope helpers use regular
  // function syntax + `this.<name>(...)` (el.scope.foo() binds `this` to
  // el.scope — object-literal properties can't see each other as bare
  // identifiers otherwise).
  scope: {
    // NO standing provider/model catalog renders here any more — the model
    // dropdown owns model choice (AiModelSwitcherBody, the single chooser).
    // The workspace-pin picker REUSES that dropdown's shared catalog
    // (functions/aiModelCatalog.js, assigned below as scope VALUES) behind an
    // on-demand `if:` gate — a different QUESTION (the workspace pin, never
    // root.modelMode), the same rows. Do not reintroduce a copy.
    PIN_CATALOG: AI_MODEL_ROWS,
    catalogRows: aiModelCatalogRows,

    // ── keyboard (2026-09-02) ─────────────────────────────────────────────
    // THE shared menu keyboard model (functions/menuKeyboard.js), assigned as
    // scope VALUES exactly like the catalog above — a scope method that
    // called them by their bare imported names would read as undefined after
    // a frank to-json lift.
    //
    // What this panel takes and what it deliberately does NOT:
    //   • Escape closes the panel from anywhere inside it, on BOTH hosts.
    //   • Arrows cycle WITHIN one section (Authorization, Voice, the pinned-
    //     model picker) and left/right across the "Applies to" segments.
    //   • The pinned-model picker is a real menu: focus enters on open,
    //     returns to its row on close.
    //   • Rows stay in the NATIVE tab order (no roving tabindex). This body
    //     is also a drawer sheet with headings, a read-only reliability
    //     readout and several independent groups — Tab is the primary way
    //     through it, and a menu-style roving model would remove far more
    //     than it adds. The arrow cycle is additive.
    navRows: menuArrowNav,
    focusOnOpen: menuFocusOnOpen,
    nodeVisible: menuNodeVisible,
    focusBack: restoreMenuFocus,

    // Close the panel on Escape, whichever host mounts it. The chat surface
    // renders it inside SettingsDrawer behind `root.showSettings`; the shell
    // composer hands it to the @symbo.ls/dropdown plugin under the 'aiParams'
    // id (AiSettingsButton). Both are attempted, and neither harms the other:
    // the flag is already false on the shell, and `closeDropdown` is a
    // best-effort el.call that portable consumers may not register at all.
    closePanel(el, s) {
      if (s && s.root && s.root.showSettings) s.root.update({ showSettings: false })
      try {
        el.call('closeDropdown', 'aiParams')
      } catch (_) {}
      this.focusBack(el)
    },

    AUTH_ROWS: [
      { key: 'ask', label: 'ai.settings.authAsk.label', sub: 'ai.settings.authAsk.sub' },
      { key: 'auto', label: 'ai.settings.authAuto.label', sub: 'ai.settings.authAuto.sub' }
    ],

    VOICE_ROWS: [
      { key: 'on', label: 'ai.settings.voiceOn.label', sub: 'ai.settings.voiceOn.sub' },
      { key: 'off', label: 'ai.settings.voiceOff.label', sub: 'ai.settings.voiceOff.sub' }
    ],

    chooseVoice(el, s, key) {
      el.call('setSpeakReplies', key === 'on')
      // Priming inside THIS click blesses the audio element for autoplay, so
      // the very next reply can speak without another gesture.
      if (key === 'on') {
        try {
          el.call('primeSpeakAudio')
        } catch (_) {}
      }
      this.bumpTick(s.root)
    },

    isLocal(s) {
      return (s.root.targetMode || 'general') === 'local'
    },
    scopeIsWorkspace(s) {
      return s.root._aiScope === 'workspace'
    },

    // The member's role on THIS workspace as the SERVER resolved it: the held
    // record's `access.role` (getWorkspace's `callerAccess` — the read is
    // gated on the same resolution `updateWorkspaceSettings` runs before it
    // writes a pin). Never the org role (an org admin named `editor` on a
    // workspace is an editor there, and the server refuses the write) and
    // never the record's own `role` (list rows stamp a 'member' stand-in).
    // The shell's `callerWorkspaceRole` reads this same field, but not every
    // host of this shared panel registers it (the workspace-ui catalog, the
    // extensions), so the panel reads the one field itself — the same record
    // `resolveWsId` takes the write's target from. null = the answer has not
    // landed yet (the boot merges it into the held record in the background).
    workspaceRole(s) {
      const ws = s && s.root && s.root.workspace
      const role = ws && ws.access && ws.access.role
      return typeof role === 'string' && role ? role : null
    },

    // True only when that answer is owner/admin. Any other role is false, and
    // so is no answer yet: the panel WAITS — the Workspace segment stays
    // disabled, and the pin and authorization writes send nothing.
    isWorkspaceAdmin(s) {
      const role = this.workspaceRole(s)
      return role === 'owner' || role === 'admin'
    },

    // The workspace's pinned provider (settings.aiProvider), if any.
    // TWO sources, in precedence order, and both are needed:
    //   1. root.workspaceSettings.aiProvider — the WRITE MIRROR
    //      persistWorkspacePin maintains: the newest value this session, ahead
    //      of the not-yet-refetched record right after a write.
    //   2. root.workspace.settings.aiProvider — the CANONICAL server record
    //      (workspaceSettings.js is THE accessor; direct-read fallback kept
    //      for hosts without the registered function). Reading ONLY the
    //      mirror was a measured defect (2026-08-31, live): a fresh boot
    //      showed a pinned workspace as "Off", and a post-reload unpin merge
    //      built from {} and dropped the pinned modelMode/authMode siblings.
    // DECISION-site note (spec §5): while the record is still pre-arrival
    // both sources are empty and this reads "no pin" for a moment — the safe
    // action for both meanings, and writes are admin-gated anyway.
    workspacePin(el, s) {
      const mirror = s.root.workspaceSettings && s.root.workspaceSettings.aiProvider
      if (mirror) return mirror
      let bag = null
      try {
        bag = el && el.call ? el.call('workspaceSettings') : null
      } catch (_) {
        bag = null
      }
      const settings =
        bag && bag.ready ? bag.settings : (s.root.workspace && s.root.workspace.settings) || null
      return (settings && settings.aiProvider) || null
    },

    // The pin, normalized: the modelMode ONLY when the shape is the one the
    // server honours (mode 'provider' — resolveWorkspaceModelMode); null for
    // 'cloud'/'bridge'/absent, so no other shape ever reads as pinned.
    pinnedModelMode(el, s) {
      const pin = this.workspacePin(el, s)
      return pin && pin.mode === 'provider' && pin.modelMode ? pin.modelMode : null
    },
    pinLabel(mode) {
      const row = this.PIN_CATALOG.find((r) => r.key === mode)
      return (row && row.label) || mode
    },
    pinSubCopy(el, s) {
      void s.root._aiPanelTick
      if (this.pinFailed(el, s)) return el.call('polyglot', 'ai.settings.pin.saveFailed')
      const pinned = this.pinnedModelMode(el, s)
      return pinned
        ? el
            .call('polyglot', 'ai.settings.pin.activeSuffix')
            .replace('{model}', el.call('polyglot', this.pinLabel(pinned)))
        : el.call('polyglot', 'ai.settings.pin.off')
    },
    // The picker rows: the dropdown's own shared catalog + live demotion,
    // prefixed with the unpin row. ACTIVE follows the WORKSPACE PIN — a
    // different question from the dropdown's per-user root.modelMode.
    pinRows(el, s) {
      void s.root._aiPanelTick
      const pinned = this.pinnedModelMode(el, s)
      const rows = this.catalogRows(this.PIN_CATALOG, s.root.aiProviderDiagnostics)
      return [
        {
          key: 'nopin',
          label: 'ai.settings.pinNone.label',
          sub: 'ai.settings.pinNone.sub',
          active: !pinned
        },
        ...rows.map((r) => ({ ...r, active: r.key === pinned }))
      ]
    },
    togglePinMenu(el, s) {
      const root = el.state && el.state.root
      if (!root || !root.update) return
      const opening = !root._aiPinMenuOpen
      root.update({ _aiPinMenuOpen: opening })
      // Refresh the live per-provider diagnostics on OPEN only — the same
      // best-effort contract the model dropdown's trigger follows.
      if (opening) {
        try {
          el.call('fetchAiProviderDiagnostics')
        } catch (_) {}
      }
      this.bumpTick(root)
    },
    // Admin-only. 'nopin' → mode 'cloud' (the server's "no pin" — the
    // resolver falls through to each member's own request); a model key →
    // mode 'provider' + that modelMode. persistWorkspacePin's merge keeps
    // the pinned authMode either way.
    choosePin(el, s, key) {
      if (!this.isWorkspaceAdmin(s)) return
      const root = el.state && el.state.root
      if (key === 'nopin') this.persistWorkspacePin(el, s, { mode: 'cloud' })
      else this.persistWorkspacePin(el, s, { mode: 'provider', modelMode: key })
      if (root && root.update) root.update({ _aiPinMenuOpen: false })
      this.bumpTick(root)
    },

    // The SDK does NOT live on the state root (root.sdk is undefined) and
    // el.sdk isn't reliably present on a nested shared-library component, so
    // fall back to the getSDK() singleton — the SAME cached, auth-hydrated
    // instance aiService resolves through. This is what makes the admin
    // workspace-pin write actually fire.
    resolveSdk(el) {
      const fromEl = (el && el.state && el.state.root && el.state.root.sdk) || (el && el.sdk)
      if (fromEl) return fromEl
      try {
        return getSDK()
      } catch (_) {
        return null
      }
    },
    resolveWsId(el) {
      return this.resolveWsIdFrom(el.state && el.state.root, this.resolveSdk(el))
    },
    // The active workspace's id: the held record first, then the SDK's
    // context. Split from resolveWsId so a write's late answer can ask it
    // again from the root it captured (the row that started the write may
    // be gone by then). Not the packages' `wsIdOf`: that reads the record
    // only, and a shared component imports no package function.
    resolveWsIdFrom(root, sdk) {
      return (
        (root && root.workspace && (root.workspace._id || root.workspace.id)) ||
        (sdk && sdk._context && sdk._context.activeWorkspaceId) ||
        null
      )
    },

    bumpTick(root) {
      if (root && root.update) root.update({ _aiPanelTick: (root._aiPanelTick || 0) + 1 })
    },

    // Admin-only: write settings.aiProvider so the SERVER applies this
    // provider to every member's turns. Merge-safe single-key PATCH; mirrors
    // into root.workspaceSettings so the UI reflects it immediately.
    //
    // A REFUSED write (a 403, a failed request, a `{ success: false }`
    // answer — onboarding.js's language save reads the same shape) puts the
    // mirror's pin back to its value before the write and notes the refusal
    // for the pin row (`pinError` on the panel's own store). The mirror never
    // keeps a pin the server did not save: the row, the picker and the
    // transport's authMode all read it. Root, SDK and the panel store are
    // captured NOW — the picker row that started the write unmounts when the
    // picker closes, long before a slow answer lands.
    persistWorkspacePin(el, s, aiProvider) {
      const root = el.state && el.state.root
      const sdk = this.resolveSdk(el)
      const wsId = this.resolveWsId(el)
      if (!sdk || !sdk.updateWorkspaceSettings || !wsId) return
      const merged = { ...(this.workspacePin(el, s) || {}), ...aiProvider }
      const before = this.mirrorPin(root)
      const panel = this.panelStore(s)
      if (panel && panel.pinError) panel.update({ pinError: null })
      if (root && root.update) {
        root.update({
          workspaceSettings: { ...(root.workspaceSettings || {}), aiProvider: merged }
        })
      }
      // The executor runs at once, so the request still leaves inside this
      // click, and a synchronous throw takes the same refusal path.
      new Promise((resolve) => resolve(sdk.updateWorkspaceSettings(wsId, { aiProvider: merged })))
        .then((res) => {
          if (res && res.success === false) throw new Error(res.message || '')
        })
        .catch((err) => {
          console.warn('[AiSettingsPanel] workspace pin write failed:', (err && err.message) || err)
          if (!this.undoWorkspacePin(root, sdk, wsId, merged, before)) return
          if (panel && panel.update) panel.update({ pinError: wsId })
        })
    },

    // Puts the mirror's pin back after a refused write — only while the
    // refused value is still what the mirror shows, and only on the workspace
    // the write was for. A newer write (its own answer decides) or a
    // workspace switch (the mirror belongs to the other workspace now) is
    // left alone, and then nothing is noted either. Values, not identity: the
    // store wraps every written object in a proxy of its own. Answers
    // whether it put the pin back.
    undoWorkspacePin(root, sdk, wsId, merged, before) {
      if (!root || !root.update) return false
      if (String(this.resolveWsIdFrom(root, sdk)) !== String(wsId)) return false
      const bag = root.workspaceSettings
      if (!this.samePin(bag && bag.aiProvider, merged)) return false
      root.update({ workspaceSettings: { ...bag, aiProvider: before } })
      this.bumpTick(root)
      return true
    },
    // A plain copy of the mirror's pin (null when the mirror holds none) —
    // the value a refused write puts back. null lets workspacePin fall
    // through to the canonical record again.
    mirrorPin(root) {
      const pin = root && root.workspaceSettings && root.workspaceSettings.aiProvider
      return pin ? { ...pin } : null
    },
    // Same keys, same values — the aiProvider shape is flat.
    samePin(a, b) {
      if (!a || !b) return false
      const keys = Object.keys(b)
      return Object.keys(a).length === keys.length && keys.every((k) => a[k] === b[k])
    },
    // The panel's OWN store (the body's `state: { pinError }`). The pin row
    // holds it as its state; a picker or authorization ROW holds its item as
    // state, and the panel's store is that row store's parent. Never the app
    // root: a refused write is this panel's note, not shell state.
    panelStore(s) {
      if (s && 'pinError' in s) return s
      const parent = s && s.parent
      return parent && 'pinError' in parent ? parent : null
    },
    // True while this panel's last workspace write stands refused, on the
    // workspace it was for (another workspace's row reads normally).
    pinFailed(el, s) {
      const failed = s && s.pinError
      return !!failed && String(failed) === String(this.resolveWsId(el))
    },

    // The Local-agent row is a TOGGLE, and the ONLY exit from local mode: the
    // model dropdown hides itself while local (Chat.js Actions `if:`, app.js
    // ModelBtn `hide:`), and the provider list that used to carry the exit is
    // deleted. ON → select the local target and open the connect setup as a
    // shell-root modal overlay (AiBridgeModal), NOT inline, so the dropdown
    // never grows/shifts. OFF → back to the cloud target, keeping whatever
    // modelMode the dropdown last stored, and close the connect modal.
    chooseLocal(el, s) {
      const root = el.state && el.state.root
      if (!root || !root.update) return
      // Inlined `isLocal(s)`'s body: this helper is one line, and an inlined
      // predicate survives a frank `to-json` lift into `globalScope` (which
      // does not carry same-file sibling helpers along —
      // `unresolvable-identifier`, the trap chooseProvider documented before
      // it was deleted).
      const leaving = (s.root.targetMode || 'general') === 'local'
      const next = leaving ? 'workspace' : 'local'
      root.update({ targetMode: next, _aiBridgeSetupOpen: !leaving })
      try {
        el.call('onTargetModeChange', next)
      } catch (_) {}
      this.bumpTick(root)
    },

    chooseAuth(el, s, key) {
      const root = el.state && el.state.root
      // `el.call('getAiService')`, not the bare imported function —
      // chooseAuth is referenced from multiple element sites in this file,
      // so frank `to-json` auto-lifts it into `globalScope`, and the lift's
      // text extraction drops the top-of-file import binding
      // (`unresolvable-identifier`, same class as getSDK.js's `SDK` import —
      // see tickets/smbls.md). `el.call` resolves through the live functions
      // registry at runtime instead of a static free-var reference, so it
      // survives the lift untouched. `getAiService` is already barrelled
      // (functions/index.js `export * from './aiService.js'`).
      const ai = el.call('getAiService')
      try {
        if (ai) ai.setAuthMode(key)
      } catch (_) {}
      if (this.scopeIsWorkspace(s) && this.isWorkspaceAdmin(s)) {
        const pin = this.workspacePin(el, s) || {}
        this.persistWorkspacePin(el, s, {
          mode: pin.mode || 'cloud',
          modelMode: pin.modelMode || 'auto',
          authMode: key
        })
      }
      this.bumpTick(root)
    },

    // AI-210 — one source of truth for both the SDK call param and the
    // display copy below, so the two never drift.
    AI_RELIABILITY_DAYS: 14,

    // Best-effort load for ReliabilitySection.Row. `turnStats` lives on the
    // AI SERVICE (shipped sdk e7b88ec: AiService.turnStats, `GET
    // .../ai-turn-stats`). The SDK keeps services in a Map — there is NO
    // `sdk.ai` property (live-verified 2026-08-16: `sdk.ai` undefined on the
    // running page while the resolved service had `turnStats`; a property
    // feature-detect silently gated this row off on a working stack). Reach
    // it the way every other row in THIS panel does — the app-cached
    // resolved instance via `el.call('getAiService')` (see the Options rows
    // above), falling back to the async `sdk.getService('ai')` for a host
    // that has not resolved it yet. Writes onto the CALLER's own local state
    // (`el.update`, never `root.update`) — see the comment on
    // ReliabilitySection's `state: {}` for why this data must stay off the
    // shared root store.
    loadAiReliability(el) {
      // BOOT WINDOW: this panel is an always-mounted, hide-gated body, so
      // onInit runs at shell boot — before the SDK singleton exists and
      // before the AI service has resolved. A one-shot "is it there now?"
      // check silently no-ops for the whole session (live-verified
      // 2026-08-16: header rendered, row never appeared; re-invoking the
      // loader post-boot rendered it). So resolve through the app's own
      // `resolveAiService` promise — it settles once `sdk.getService('ai')`
      // completes, however long boot takes — with the sync-cached instance
      // as the fast path and `sdk.getService('ai')` as the fallback for a
      // host without that registered function.
      let cached = null
      try {
        cached = el.call('getAiService')
      } catch (_) {
        cached = null
      }
      let svcPromise
      if (cached && typeof cached.turnStats === 'function') {
        svcPromise = Promise.resolve(cached)
      } else {
        try {
          svcPromise = Promise.resolve(el.call('resolveAiService'))
        } catch (_) {
          const sdk = this.resolveSdk(el)
          svcPromise =
            sdk && typeof sdk.getService === 'function'
              ? Promise.resolve(sdk.getService('ai'))
              : Promise.resolve(sdk && sdk.ai)
        }
      }
      svcPromise
        .then((ai) => {
          if (!ai || typeof ai.turnStats !== 'function') return null
          return ai.turnStats({
            workspaceId: this.resolveWsId(el),
            days: this.AI_RELIABILITY_DAYS
          })
        })
        .then((resp) => {
          if (resp == null) return
          // Defensive unwrap — mirrors usage.js's `_unwrapTurnStats`
          // (`r.data || r`): tolerate either an envelope-wrapped or a flat
          // resolved shape rather than assuming one.
          const data = (resp && resp.data) || resp || {}
          const rows = Array.isArray(data.rows) ? data.rows : []
          // STATE write, not `el.update`: `el.update({ aiReliability: {…} })`
          // routes a lowercase key with an object value through the
          // element-patch path (a child/prop write), so the value never
          // reached this section's local store and `Row.if` never flipped —
          // live-verified 2026-08-16 (`state.update` mounted the row;
          // `el.update` left the store empty). The section's own
          // `state: {}` branch is what makes this write local.
          if (el.state && typeof el.state.update === 'function') {
            el.state.update({ aiReliability: this.formatAiReliability(el, rows) })
          }
        })
        .catch((err) => {
          console.warn('[AiSettingsPanel] turnStats fetch failed:', (err && err.message) || err)
        })
    },

    // Formatter — aggregates the day-window's rows into the row's two
    // display lines, in the active language (`el` reads polyglot). `rows` is
    // asserted to always be an array (never undefined): loadAiReliability
    // only reaches here on a SUCCESSFUL response, so an empty array is the
    // genuine "nothing recorded yet" case (AiService.turnStats's own asserted
    // contract), never a stand-in for a failed fetch. The element comes
    // FIRST, as the loader passes it (dev 2026-10-30: the signature kept only
    // `rows` after the i18n extraction, so `rows` was the element and every
    // open logged "turnStats fetch failed: … reduce is not a function").
    formatAiReliability(el, rows) {
      const totals = rows.reduce(
        (acc, r) => ({
          toolTurns: acc.toolTurns + (Number(r.toolTurns) || 0),
          okToolTurns: acc.okToolTurns + (Number(r.okToolTurns) || 0),
          textLeakTurns: acc.textLeakTurns + (Number(r.textLeakTurns) || 0)
        }),
        { toolTurns: 0, okToolTurns: 0, textLeakTurns: 0 }
      )
      if (totals.toolTurns === 0) {
        return {
          label: el.call('polyglot', 'ai.settings.reliability.empty'),
          sub: el
            .call('polyglot', 'ai.settings.reliability.window')
            .replace('{days}', String(this.AI_RELIABILITY_DAYS))
        }
      }
      const pct = Math.round((totals.okToolTurns / totals.toolTurns) * 100)
      const sub =
        totals.textLeakTurns > 0
          ? el
              .call(
                'polyglot',
                totals.textLeakTurns === 1
                  ? 'ai.settings.reliability.leak'
                  : 'ai.settings.reliability.leaks'
              )
              .replace('{count}', String(totals.textLeakTurns))
          : el.call('polyglot', 'ai.settings.reliability.noLeaks')
      return {
        label: el
          .call('polyglot', 'ai.settings.reliability.label')
          .replace('{pct}', String(pct))
          .replace('{ok}', String(totals.okToolTurns))
          .replace('{total}', String(totals.toolTurns))
          .replace('{days}', String(this.AI_RELIABILITY_DAYS)),
        sub
      }
    }
  },
  tag: 'div',
  // The panel's OWN store (`state: {}` + `s.update`, the ReliabilitySection
  // precedent above): `pinError` holds the workspace id of a refused
  // workspace write, read by the pin row. Local on purpose: each host (the
  // composer dropdown, the sidebar sheet) shows the note where the click
  // happened, and an undeclared ROOT key would be shell state nobody owns.
  // The sections declare no `state:`, so they hold this store by reference;
  // every `s.root.X` read still reaches the app root (the store's `root`
  // getter climbs past local branches).
  state: { pinError: null },
  // Escape closes the panel from ANY row inside it (2026-09-02). It sits on
  // the body, not on each section, because DOMQL's keydown delegate walks the
  // ancestor chain of `event.target` — one handler here catches every
  // descendant, and a section that stops its own key (the pin picker's own
  // Escape) simply never lets it reach here.
  role: 'group',
  ariaLabel: (el) => el.call('polyglot', 'ai.settings.panelLabel'),
  onKeydown: (e, el, s) => {
    if (e.key !== 'Escape') return
    e.preventDefault()
    el.scope.closePanel(el, s)
  },
  // Spacing token (~the old 320px) — hosts that must shrink below it (the
  // in-sidebar SettingsDrawer sheet) override minWidth: '0' at the mount.
  minWidth: 'G',
  maxWidth: '90vw',
  flow: 'y',
  gap: 'A',
  textAlign: 'left',
  // No diagnostics fetch here any more: the per-provider diagnostics rendered
  // ONLY the deleted provider rows. The data path itself is untouched — the
  // model dropdown still refreshes it on its own open (AiModelSwitcherTrigger
  // onClick and the shell composer's ModelBtn onClick both call
  // `fetchAiProviderDiagnostics`), which is where the reason badges render.
  AnswersSection,
  ScopeSection,
  PinSection,
  // ProviderSection is unmounted (2026-09-02 per Nika "move AI provider
  // section to model switcher both in AI prompt and sidebar") — the Local
  // agent row now lives in AiModelSwitcherBody, which both surfaces render.
  // The const stays declared below for hosts that still mount it by key.
  AuthSection,
  VoiceSection,
  ReliabilitySection
}

export default AiSettingsPanel
